<template>
  <div :class="[classes.listCardWrapper, 'card']">
    <ResizeListener :class="[classes.resize]" :onResize="onResize" />
    <div :class="[classes.absolute]">
      <CardHeader :class="classes.datesport" :eventId="eventId" />
    </div>
    <div :class="[classes.absolute, classes.marketName]">
      <MarketName
        :marketId="market.marketId"
        :fallbackName="market.marketName"
        :class="['market-name']"
      />
    </div>

    <div :class="classes.cardContent">
      <EventNames
        :class="classes.leftContent"
        :eventId="eventId"
        :marketId="marketId"
      />

      <div :class="classes.listWrapper">
        <div :class="classes.list" :style="listWrapperStyles">
          <ListOption
            v-for="(option, i) in options"
            :key="option.id"
            :stagger="i"
            :eventId="eventId"
            :option="option"
            :marketId="market.marketId"
          />
        </div>
        <div :class="classes.fade" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { defineComponent, reactive, computed, ComputedRef } from "vue";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";

import ListOption from "../ListOption.vue";
import EventNames from "../../../EventNamesBadgesStacked.vue";
import MarketName from "../../../MarketName.vue";
import CardHeader from "../../../CardHeaderLeaderboard.vue";
import ResizeListener from "../../../ResizeListener.vue";

import {
  composeMarket,
  composeListMarketOptions,
  composeEvent
} from "@fresh8/ad-data-composition/src/betData";
import { Brand } from "@fresh8/types/src";
import composeDate from "@fresh8/ad-data-composition/src/date";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { Market, SportEvent, Option } from "@fresh8/types/src/betData";

const useStyles = createUseStyles({
  absolute: {
    position: "absolute",
    width: "40%"
  },
  cardHeaderWrapper: {
    display: "flex",
    position: "relative",
    width: "100%",
    boxSizing: "border-box",
    alignItems: "center"
  },
  datesport: {
    "& span": {
      fontSize: "10px !important"
    }
  },
  marketName: {
    right: 8,
    textAlign: "right",

    "& .market-name": {
      display: "flex",
      justifyContent: "flex-end",
      height: "17px",
      alignItems: "center"
    }
  },
  listCardWrapper: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    height: "100%",
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    background: props => props.cardBackgroundColor,
    borderRadius: props => `${props.borderRadius}px`,
    position: "relative",
    boxSizing: "border-box"
  },
  cardContent: {
    display: "flex",
    flex: 1
  },

  leftContent: {
    margin: "17px 0 6px",
    paddingTop: "6px",
    paddingBottom: "6px"
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1
  },
  listWrapper: {
    flex: "1 0 50%",
    position: "relative",
    overflowX: "hidden",
    paddingTop: "14px",
    borderLeft: props => `1px solid ${props.marketBorderColor}`
  },
  list: {
    display: "grid",
    boxSizing: "border-box",
    padding: "6px 6px",
    gridAutoFlow: "row",
    overflowX: "auto",
    gap: "4px",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "repeat(auto-fit, max(24px))",
    gridAutoRows: "max(24px)",
    "&  .odds-text": {
      fontSize: "11px"
    },
    "&  .name": {
      fontSize: "10px"
    }
  },
  fade: {
    pointerEvents: "none",
    position: "absolute",
    height: "15px",
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: "0 0 4px 4px",
    background: props =>
      props.backgroundImage
        ? "none"
        : `linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${props.cardBackgroundColor ||
            props.backgroundColor} 79.17%)`
  },
  spacer: {
    display: "flex",
    height: "5px"
  },
  resize: {
    pointerEvents: "none",
    zIndex: -99
  }
});

export default defineComponent({
  name: "SmallResponsive",
  components: {
    ListOption,
    CardHeader,
    EventNames,
    MarketName,
    ResizeListener
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const {
      adTemplateBranding,
      marketBranding,
      backgroundImageSets
    } = injectBrand();

    const backgroundImage = computed(() => {
      return !!Object.keys(backgroundImageSets?.value || {}).length;
    });

    const carouselContext = injectCarouselContext();

    const relativeStyles = computed(() => ({
      ...adTemplateBranding.value,
      backgroundImage: backgroundImage.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(relativeStyles);
    const market = composeMarket(props.marketId) as ComputedRef<Market>;
    const marketOptions = composeListMarketOptions(
      props.marketId
    ) as ComputedRef<Option[]>;
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const fixtureStartDate = composeDate(event.value.id);

    const listWrapperStyles = reactive({
      height: "0"
    });

    const eventNameStyles = reactive({
      height: "0"
    });

    // These only come from inspecting the elements in browser.
    const elementHeights = {
      cardHeader: 17,
      marketName: 10,
      margin: 4,
      leftContentPadding: 12
    };

    const onResize = ({ height, width }: { height: number; width: number }) => {
      listWrapperStyles.height = height - elementHeights.cardHeader + "px";
      eventNameStyles.height =
        height -
        elementHeights.cardHeader -
        elementHeights.marketName -
        elementHeights.margin -
        elementHeights.leftContentPadding +
        "px";

      return { height, width };
    };

    return {
      eventNameStyles,
      listWrapperStyles,
      market,
      marketBranding,
      classes: classesRef,
      options: marketOptions,
      event,
      fixtureStartDate,
      onResize,

      marketNameStyle: {
        get(brand: Brand) {
          return brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureTimeFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      fixtureNameStyle: {
        get: (brand: Brand) => brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
